import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, TextField, Button, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Grid, Chip, Stack, Divider, Avatar, InputAdornment, CardHeader, Alert
} from "@mui/material";
import { 
  CalendarToday, 
  TrendingUp, 
  TrendingDown, 
  LocalShipping, 
  Inventory, 
  Assignment,
  Receipt,
  Download,
  Refresh
} from "@mui/icons-material";
import { getLinenDailyReport } from "../../services/api";
import { useSnackbar } from "notistack";

export const LinenDailyReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await getLinenDailyReport(date);

      // Support both formats: {report:{}} or direct {}
      const data = res.data?.report || res.data;
      setReport(data);
      
      enqueueSnackbar("🎉 Report loaded successfully", { variant: "success" });
    } catch (err) {
      console.error("Failed to load report:", err);
      enqueueSnackbar("❌ Failed to load report", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadReport(); 
  }, []);

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!report) return null;
    
    const totalIssues = (report.issues || []).reduce((sum, item) => sum + (item.qty || 0), 0);
    const totalReturns = (report.returns || []).reduce((sum, item) => sum + (item.qty || 0), 0);
    const totalDispatches = report.dispatches?.length || 0;
    const totalReceives = report.receives?.length || 0;

    return { totalIssues, totalReturns, totalDispatches, totalReceives };
  };

  const stats = getSummaryStats();

  const handleExport = () => {
    enqueueSnackbar("📊 Export feature coming soon!", { variant: "info" });
  };

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Assignment sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Daily Linen Report
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Comprehensive overview of daily linen operations
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Download sx={{ fontSize: 28 }} />}
                onClick={handleExport}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                label="Select Date"
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: '1.2rem', fontWeight: 600 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: "primary.main", fontSize: 28 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.2rem',
                    '& input': {
                      padding: '16px 14px',
                      fontSize: '1.2rem',
                      fontWeight: 600
                    }
                  }
                }}
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={8}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button 
                  variant="contained" 
                  onClick={loadReport}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={24} /> : <Refresh sx={{ fontSize: 28 }} />}
                  sx={{ 
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    minWidth: 160
                  }}
                >
                  {loading ? 'Loading...' : 'Load Report'}
                </Button>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          py: 8, 
          flexDirection: 'column',
          gap: 3 
        }}>
          <CircularProgress size={80} thickness={4} />
          <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: 'primary.main' }}>
            Generating Daily Report...
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
            Please wait while we compile your data
          </Typography>
        </Box>
      )}

      {/* Summary Cards */}
      {!loading && stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '3rem', fontWeight: 800, mb: 1 }}>
                  {stats.totalIssues}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 600, opacity: 0.9 }}>
                  Items Issued
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <TrendingDown sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '3rem', fontWeight: 800, mb: 1 }}>
                  {stats.totalReturns}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 600, opacity: 0.9 }}>
                  Items Returned
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <LocalShipping sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '3rem', fontWeight: 800, mb: 1 }}>
                  {stats.totalDispatches}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 600, opacity: 0.9 }}>
                  Vendor Dispatches
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <Inventory sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '3rem', fontWeight: 800, mb: 1 }}>
                  {stats.totalReceives}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 600, opacity: 0.9 }}>
                  Items Received
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Report Display */}
      {!loading && report && (
        <Grid container spacing={3}>
          {/* Issues Table */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUp sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Issues
                    </Typography>
                    <Chip 
                      label={report.issues?.length || 0} 
                      size="medium" 
                      color="primary"
                      sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                    />
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'primary.light'
                }}
              />
              <CardContent sx={{ p: 0 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'grey.100' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📋 Item Name
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.issues || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              <TrendingUp sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                              <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
                                No Issues Recorded
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                No linen issues for the selected date
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (report.issues || []).map((item, idx) => (
                          <TableRow 
                            key={idx} 
                            hover
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.3s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell sx={{ fontSize: '1.1rem', py: 2 }}>
                              {item.itemName || item.item || 'Unknown Item'}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Chip 
                                label={item.qty} 
                                size="medium" 
                                variant="outlined" 
                                color="primary"
                                sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Returns Table */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingDown sx={{ fontSize: 32, color: 'success.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Returns
                    </Typography>
                    <Chip 
                      label={report.returns?.length || 0} 
                      size="medium" 
                      color="success"
                      sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                    />
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'success.light'
                }}
              />
              <CardContent sx={{ p: 0 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'grey.100' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📋 Item Name
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.returns || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              <TrendingDown sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                              <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
                                No Returns Recorded
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                No linen returns for the selected date
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (report.returns || []).map((item, idx) => (
                          <TableRow 
                            key={idx} 
                            hover
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': {
                                backgroundColor: 'success.light',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.3s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell sx={{ fontSize: '1.1rem', py: 2 }}>
                              {item.itemName || item.item || 'Unknown Item'}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Chip 
                                label={item.qty} 
                                size="medium" 
                                variant="outlined" 
                                color="success"
                                sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Dispatches Section */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalShipping sx={{ fontSize: 32, color: 'warning.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Dispatches to Vendors
                    </Typography>
                    <Chip 
                      label={report.dispatches?.length || 0} 
                      size="medium" 
                      color="warning"
                      sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                    />
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'warning.light'
                }}
              />
              <CardContent>
                {(!report.dispatches || report.dispatches.length === 0) ? (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
                    <LocalShipping sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
                      No Dispatches Recorded
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      No vendor dispatches for the selected date
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    {report.dispatches.map((dispatch, idx) => (
                      <Paper 
                        key={idx} 
                        variant="outlined" 
                        sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          borderLeft: '4px solid',
                          borderLeftColor: 'warning.main',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Stack spacing={2}>
                          <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 700, color: 'warning.main' }}>
                            Dispatch: {dispatch.dispatchNumber || 'N/A'}
                          </Typography>
                          <Divider />
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                🏢 Vendor: {dispatch.vendor?.name || 'Unknown Vendor'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                📦 Items: {dispatch.items?.length || 0}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                📊 Status: 
                                <Chip 
                                  label={dispatch.status} 
                                  size="medium"
                                  color={
                                    dispatch.status === 'received' ? 'success' : 
                                    dispatch.status === 'partial_received' ? 'warning' : 'default'
                                  }
                                  sx={{ 
                                    ml: 2, 
                                    fontSize: '1rem', 
                                    fontWeight: 700,
                                    minWidth: 120
                                  }}
                                />
                              </Typography>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Receives Section */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Inventory sx={{ fontSize: 32, color: 'info.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Receives from Vendors
                    </Typography>
                    <Chip 
                      label={report.receives?.length || 0} 
                      size="medium" 
                      color="info"
                      sx={{ fontSize: '1.1rem', fontWeight: 700, minWidth: 60 }}
                    />
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'info.light'
                }}
              />
              <CardContent>
                {(!report.receives || report.receives.length === 0) ? (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
                    <Inventory sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
                      No Receives Recorded
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      No vendor receives for the selected date
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    {report.receives.map((receive, idx) => (
                      <Paper 
                        key={idx} 
                        variant="outlined" 
                        sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          borderLeft: '4px solid',
                          borderLeftColor: 'info.main',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Stack spacing={2}>
                          <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 700, color: 'info.main' }}>
                            Receive ID: {receive._id?.slice(-8) || 'N/A'}
                          </Typography>
                          <Divider />
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                🏢 Vendor: {receive.vendor?.name || 'Unknown Vendor'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                📅 Received: {new Date(receive.received_at).toLocaleDateString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                📦 Items: {receive.items?.length || 0}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Empty State */}
      {!loading && !report && (
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          textAlign: 'center',
          py: 8
        }}>
          <CardContent>
            <Assignment sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 3 }} />
            <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              No Report Data
            </Typography>
            <Typography variant="h6" sx={{ fontSize: '1.3rem', color: 'text.secondary', mb: 4 }}>
              Select a date and click "Load Report" to generate your daily linen report
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={loadReport}
              sx={{ 
                fontSize: '1.2rem',
                px: 4,
                py: 1.5,
                borderRadius: 3
              }}
            >
              Generate Report
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};